/**
 * useSessionTimeout — client-side idle detection that complements the
 * server-authoritative session policy.
 *
 * Mounts inside `ProtectedRoute` so it ONLY runs for authenticated users.
 *
 * Behavior (numbers come from `GET /api/auth/session-policy`):
 *   1. On mount: fetch the policy (idle / absolute / warning seconds).
 *   2. Track user activity: mousemove, mousedown, keydown, touchstart, scroll, click.
 *      Each event resets a `lastActivity` ref (no React re-render on every event).
 *   3. Every 5 seconds, check the idle window:
 *        - if (idle - warning) < elapsed < idle → show a sonner warning toast
 *          ("You'll be signed out soon. Move anywhere to stay signed in.").
 *        - if elapsed >= idle → call /api/auth/logout, clear React-Query cache
 *          via the auth context, and route to /app/login?reason=idle.
 *   4. Also handles the absolute cap: if `Date.now() - mountedAt >= absolute_timeout`
 *      the session is forced out the same way.
 *
 * If the API itself returns 401 (server already invalidated) the global 401
 * handler in api.js will fire `onForcedLogout()` directly — this hook just
 * stays out of the way in that case.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { authApi, setOn401Handler } from './api';
import { useAuth } from './AuthContext';
import { logger } from '../lib/logger';

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'click'];
const TICK_MS = 5000;

const REASON_COPY = {
  session_idle_timeout: 'You were signed out for inactivity.',
  session_absolute_timeout: 'Your session ended. Please sign in again.',
  session_expired: 'Your session expired. Please sign in again.',
  default: 'You were signed out. Please sign in again.',
};

export default function useSessionTimeout() {
  const navigate = useNavigate();
  const auth = useAuth();
  const lastActivityRef = useRef(Date.now());
  const warnedRef = useRef(false);
  const mountedAtRef = useRef(Date.now());
  const policyRef = useRef(null);
  const isForcingOutRef = useRef(false);

  useEffect(() => {
    let alive = true;
    let intervalId = null;
    let warningToastId = null;

    const onActivity = () => {
      lastActivityRef.current = Date.now();
      if (warnedRef.current) {
        warnedRef.current = false;
        if (warningToastId) {
          toast.dismiss(warningToastId);
          warningToastId = null;
        }
      }
    };

    const forceLogout = async (reason) => {
      if (isForcingOutRef.current) return;
      isForcingOutRef.current = true;
      if (intervalId) clearInterval(intervalId);
      try {
        await authApi.logout();
      } catch (logoutErr) {
        // Server may already have invalidated the session (its own 401 path
        // already kicked us); the logout endpoint then 401s back. Proceeding
        // is correct — log the reason for devtools visibility only.
        logger.warn('[useSessionTimeout] logout call failed (continuing):', logoutErr?.message || logoutErr);
      }
      try {
        if (auth && typeof auth.refresh === 'function') {
          await auth.refresh();
        }
      } catch (refreshErr) {
        // Non-fatal: we're about to navigate away regardless.
        logger.warn('[useSessionTimeout] auth.refresh failed (continuing):', refreshErr?.message || refreshErr);
      }
      const message = REASON_COPY[reason] || REASON_COPY.default;
      toast(message);
      navigate(`/app/login?reason=${encodeURIComponent(reason || 'session_idle_timeout')}`, {
        replace: true,
      });
    };

    // ---- 401 handler: server kicked us. Skip the activity loop, just route out.
    setOn401Handler(({ detail } = {}) => {
      if (isForcingOutRef.current) return;
      forceLogout(detail || 'session_expired');
    });

    // ---- Fetch policy + start the activity loop
    (async () => {
      try {
        const policy = await authApi.sessionPolicy();
        if (!alive) return;
        policyRef.current = {
          idleMs: (policy?.idle_timeout_seconds || 30 * 60) * 1000,
          absoluteMs: (policy?.absolute_timeout_seconds || 24 * 3600) * 1000,
          warnMs: (policy?.idle_warning_seconds || 120) * 1000,
        };
      } catch {
        // Fallback to recommended defaults so the safety net still works.
        policyRef.current = { idleMs: 1800 * 1000, absoluteMs: 86400 * 1000, warnMs: 120 * 1000 };
      }
      if (!alive) return;

      ACTIVITY_EVENTS.forEach((ev) =>
        window.addEventListener(ev, onActivity, { passive: true }),
      );

      intervalId = setInterval(() => {
        if (!policyRef.current || isForcingOutRef.current) return;
        const { idleMs, absoluteMs, warnMs } = policyRef.current;
        const now = Date.now();
        const sinceActivity = now - lastActivityRef.current;
        const sinceMount = now - mountedAtRef.current;

        // Absolute cap — always wins.
        if (sinceMount >= absoluteMs) {
          forceLogout('session_absolute_timeout');
          return;
        }

        // Idle cutoff
        if (sinceActivity >= idleMs) {
          forceLogout('session_idle_timeout');
          return;
        }

        // Warning window
        if (!warnedRef.current && sinceActivity >= idleMs - warnMs) {
          warnedRef.current = true;
          const seconds = Math.max(1, Math.round((idleMs - sinceActivity) / 1000));
          warningToastId = toast(
            `You'll be signed out in ~${seconds}s for inactivity. Move anywhere to stay signed in.`,
            {
              duration: warnMs,
              action: {
                label: 'Stay signed in',
                onClick: () => {
                  onActivity();
                },
              },
            },
          );
        }
      }, TICK_MS);
    })();

    return () => {
      alive = false;
      if (intervalId) clearInterval(intervalId);
      if (warningToastId) {
        toast.dismiss(warningToastId);
      }
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
      setOn401Handler(null);
    };
    // We deliberately use [] — this hook is mounted once per authenticated session.
    // `navigate` and `auth` are stable enough that re-running would just churn timers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
